import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useWizardState } from "../hooks/useWizardState";
import { BudgetSlider } from "../components/forms/BudgetSlider";
import { StepperControl } from "../components/forms/StepperControl";
import { Input } from "../components/ui/Form/Input";
import { Listbox } from "../components/shared/form/Listbox";
import "./Step4Priorities.css";

interface PriorityOption {
  id: number;
  name: string;
  value: string;
}

const getHotelPriorities = (t: (key: string) => string): PriorityOption[] => [
  { id: 1, name: t("options.hotelPriorities.location"), value: "location" },
  { id: 2, name: t("options.hotelPriorities.qualityLuxury"), value: "quality-luxury" },
  { id: 3, name: t("options.hotelPriorities.price"), value: "price" },
  { id: 4, name: t("options.hotelPriorities.amenities"), value: "amenities" },
  { id: 5, name: t("options.hotelPriorities.other"), value: "other" },
];

const getFlightPreferences = (t: (key: string) => string): PriorityOption[] => [
  { id: 1, name: t("options.flightPreferences.directFlights"), value: "direct-flights" },
  { id: 2, name: t("options.flightPreferences.timings"), value: "timings" },
  { id: 3, name: t("options.flightPreferences.cheapest"), value: "cheapest" },
  { id: 4, name: t("options.flightPreferences.qualityEconomyPlus"), value: "quality-economy-plus-business" },
  { id: 5, name: t("options.flightPreferences.other"), value: "other" },
];

const getTransferOptions = (t: (key: string) => string): PriorityOption[] => [
  { id: 1, name: t("options.transferOptions.shared"), value: "shared-transfer" },
  { id: 2, name: t("options.transferOptions.privateCar"), value: "private-transfer-car" },
  { id: 3, name: t("options.transferOptions.privateVanSuv"), value: "private-transfer-van-suv" },
];

const BAGGAGE_OPTIONS = ["none", "hand", "checked"] as const;

const BUDGET_TYPES = ["total", "perPerson"];

export const Step4Priorities: React.FC = () => {
  const { t } = useTranslation();
  const { formData, updateFormData, errors, setFieldError } = useWizardState();

  const HOTEL_PRIORITIES = useMemo(() => getHotelPriorities(t), [t]);
  const FLIGHT_PREFERENCES = useMemo(() => getFlightPreferences(t), [t]);
  const TRANSFER_OPTIONS = useMemo(() => getTransferOptions(t), [t]);

  // Find selected hotel priorities
  const selectedHotelPriorities = useMemo(() => {
    if (!formData.hotelPriorities || formData.hotelPriorities.length === 0) return [];
    return HOTEL_PRIORITIES.filter((option) => 
      formData.hotelPriorities.includes(option.value)
    );
  }, [formData.hotelPriorities, HOTEL_PRIORITIES]);

  // Find selected flight preferences
  const selectedFlightPreferences = useMemo(() => {
    if (!formData.flightPreferences || formData.flightPreferences.length === 0) return [];
    return FLIGHT_PREFERENCES.filter((option) => 
      formData.flightPreferences.includes(option.value)
    );
  }, [formData.flightPreferences, FLIGHT_PREFERENCES]);

  // Find selected transfer preferences
  const selectedTransferPreferences = useMemo(() => {
    if (!formData.transferPreferences || formData.transferPreferences.length === 0) return [];
    return TRANSFER_OPTIONS.filter((option) => 
      formData.transferPreferences.includes(option.value)
    );
  }, [formData.transferPreferences, TRANSFER_OPTIONS]);

  const handleHotelPrioritiesChange = (options: PriorityOption[]) => {
    // Limit to 2 selections
    const limitedOptions = options.slice(0, 2);
    const values = limitedOptions.map((option) => option.value);
    updateFormData({ 
      hotelPriorities: values,
      otherHotel: values.includes("other") ? formData.otherHotel : ""
    });
    setFieldError("hotelPriorities", undefined);
  };

  const handleFlightPreferencesChange = (options: PriorityOption[]) => {
    // Limit to 2 selections
    const limitedOptions = options.slice(0, 2);
    const values = limitedOptions.map((option) => option.value);
    updateFormData({ 
      flightPreferences: values,
      otherFlight: values.includes("other") ? formData.otherFlight : ""
    });
    setFieldError("flightPreferences", undefined);
  };

  const handleTransferPreferencesChange = (options: PriorityOption[]) => {
    const values = options.map((option) => option.value);
    updateFormData({ transferPreferences: values });
    setFieldError("transferPreferences", undefined);
  };

  const showOtherHotel = formData.hotelPriorities?.includes("other") || false;
  const showOtherFlight = formData.flightPreferences?.includes("other") || false;

  // Check if hotel/accommodation, flights, and transfers options are selected in Step 3
  const hasHotelOption = formData.packageOptions?.includes("hotel-accommodation") || false;
  const hasFlightOption = formData.packageOptions?.includes("flights") || false;
  const hasTransferOption = formData.packageOptions?.includes("transfers") || false;

  return (
    <div className="step4-priorities">
      <h2 className="step-heading">{t("step4.heading")}</h2>

      <div className="form-group">
        {hasHotelOption && (
          <>
            <div className="form-group">
              <Listbox
                data={HOTEL_PRIORITIES}
                value={selectedHotelPriorities}
                onChange={handleHotelPrioritiesChange}
                displayField="name"
                placeholder={t("step4.hotelPriorities.placeholder")}
                label={t("step4.hotelPriorities.label")}
                multiple
                error={errors.hotelPriorities}
                required={hasHotelOption}
              />
            </div>

            {showOtherHotel && (
              <div className="conditional-field animate-slide-down">
                <Input
                  id="otherHotel"
                  label={t("step4.hotelPriorities.other.label")}
                  placeholder={t("step4.hotelPriorities.other.placeholder")}
                  value={formData.otherHotel || ""}
                  onChange={(e) => {
                    updateFormData({ otherHotel: e.target.value });
                    setFieldError("otherHotel", undefined);
                  }}
                  error={errors.otherHotel}
                />
              </div>
            )}
          </>
        )}

        {hasFlightOption && (
          <>
            <div className="form-group">
              <Listbox
                data={FLIGHT_PREFERENCES}
                value={selectedFlightPreferences}
                onChange={handleFlightPreferencesChange}
                displayField="name"
                placeholder={t("step4.flightPreferences.placeholder")}
                label={t("step4.flightPreferences.label")}
                multiple
                error={errors.flightPreferences}
                required={hasFlightOption}
              />
            </div>

            {showOtherFlight && (
              <div className="conditional-field animate-slide-down">
                <Input
                  id="otherFlight"
                  label={t("step4.flightPreferences.other.label")}
                  placeholder={t("step4.flightPreferences.other.placeholder")}
                  value={formData.otherFlight || ""}
                  onChange={(e) => {
                    updateFormData({ otherFlight: e.target.value });
                    setFieldError("otherFlight", undefined);
                  }}
                  error={errors.otherFlight}
                />
              </div>
            )}
          </>
        )}

        {hasFlightOption && (
          <fieldset className="form-fieldset">
            <legend className="fieldset-legend baggage-legend">
              {t("step4.baggage.label")}
              <span className="required-indicator" aria-label="required">*</span>
            </legend>
            <div className="checkbox-group">
              {BAGGAGE_OPTIONS.map((option) => (
                <label key={option} className="checkbox-label">
                  <input
                    type="checkbox"
                    value={option}
                    checked={(formData.baggageOptions || []).includes(option)}
                    onChange={() => {
                      const current = formData.baggageOptions || [];
                      let updated: string[];
                      if (option === "none") {
                        updated = current.includes("none") ? [] : ["none"];
                      } else {
                        const withoutNone = current.filter((v) => v !== "none");
                        updated = withoutNone.includes(option)
                          ? withoutNone.filter((v) => v !== option)
                          : [...withoutNone, option];
                      }
                      updateFormData({ baggageOptions: updated });
                      setFieldError("baggageOptions", undefined);
                    }}
                    className="checkbox-input"
                  />
                  <span className="checkbox-custom"></span>
                  <span className="checkbox-text">
                    {t(`options.baggageOptions.${option}`)}
                  </span>
                </label>
              ))}
            </div>
            {errors.baggageOptions && (
              <div className="field-error" role="alert">
                {errors.baggageOptions}
              </div>
            )}

            {(formData.baggageOptions || []).includes("hand") && (
              <div className="conditional-field animate-slide-down">
                <StepperControl
                  id="handBaggageCount"
                  label={t("step4.baggage.handBaggageCount")}
                  value={formData.handBaggageCount || 1}
                  onChange={(value) => updateFormData({ handBaggageCount: value })}
                  min={1}
                  max={20}
                />
              </div>
            )}

            {(formData.baggageOptions || []).includes("checked") && (
              <div className="conditional-field animate-slide-down">
                <StepperControl
                  id="checkedBaggageCount"
                  label={t("step4.baggage.checkedBaggageCount")}
                  value={formData.checkedBaggageCount || 1}
                  onChange={(value) => updateFormData({ checkedBaggageCount: value })}
                  min={1}
                  max={20}
                />
              </div>
            )}
          </fieldset>
        )}

        {hasTransferOption && (
          <div className="form-group">
            <Listbox
              data={TRANSFER_OPTIONS}
              value={selectedTransferPreferences}
              onChange={handleTransferPreferencesChange}
              displayField="name"
              placeholder={t("step4.transferPreferences.placeholder")}
              label={t("step4.transferPreferences.label")}
              multiple
              error={errors.transferPreferences}
            />
          </div>
        )}

        <fieldset className="form-fieldset">
          <legend className="fieldset-legend">{t("step4.budget.label")}</legend>
          <BudgetSlider
            id="budget"
            value={formData.budget}
            onChange={(value) => {
              updateFormData({ budget: value });
              setFieldError("budget", undefined);
            }}
            min={500}
            max={10000}
          />
          {errors.budget && (
            <div className="field-error" role="alert">
              {errors.budget}
            </div>
          )}

          <div className="radio-buttons-group budget-type-group">
            {BUDGET_TYPES.map((type) => (
              <label key={type} className="radio-button-label">
                <input
                  type="radio"
                  name="budgetType"
                  value={type}
                  checked={formData.budgetType === type}
                  onChange={(e) => updateFormData({ budgetType: e.target.value as "total" | "perPerson" })}
                  className="radio-button-input"
                />
                <span className="radio-button-custom"></span>
                <span className="radio-button-text">
                  {type === "total" ? t("step4.budgetType.total") : t("step4.budgetType.perPerson")}
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        <Input
          id="additionalNotes"
          label={t("step4.additionalNotes.label")}
          placeholder={t("step4.additionalNotes.placeholder")}
          component="textarea"
          value={formData.additionalNotes || ""}
          onChange={(e) => updateFormData({ additionalNotes: e.target.value })}
          rows={3}
        />
      </div>
    </div>
  );
};

